import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Heart,
  Gem,
  Users,
  MapPin,
  Sparkles,
  Shield,
  HandHeart,
  Leaf,
  Award,
  ArrowRight,
  Quote
} from 'lucide-react';

// Core values
const CORE_VALUES = [
  {
    icon: MapPin,
    title: 'Italianità Autentica',
    description: 'Ogni creazione su Rarifex nasce dalle mani di artigiani italiani. Non semplici prodotti, ma espressioni di una cultura millenaria che ha fatto dell\'eccellenza il suo tratto distintivo.',
    color: 'from-green-500 to-green-600'
  },
  {
    icon: HandHeart,
    title: 'Cura Artigianale',
    description: 'Dietro ogni oggetto c\'è una storia, un\'attenzione ai dettagli che solo le mani esperte sanno dare. Ogni imperfezione è un segno di autenticità, ogni variazione un tratto distintivo.',
    color: 'from-amber-500 to-amber-600'
  },
  {
    icon: Shield,
    title: 'Qualità Garantita',
    description: 'Selezioniamo con cura ogni artigiano e verifichiamo la qualità delle creazioni. Il nostro impegno è offrirti solo prodotti che rispettano gli standard più elevati.',
    color: 'from-[#008080] to-[#006666]'
  },
  {
    icon: Users,
    title: 'Comunità e Relazioni',
    description: 'Crediamo nel valore delle connessioni dirette. Qui non sei un numero: sei parte di una comunità che condivide la passione per il bello fatto a mano.',
    color: 'from-rose-500 to-rose-600'
  }
];

// Commitments
const COMMITMENTS = [
  {
    title: 'Trasparenza totale',
    description: 'Saprai sempre chi ha realizzato il tuo acquisto, dove e come.'
  },
  {
    title: 'Prezzi giusti',
    description: 'Gli artigiani vengono retribuiti equamente per il loro lavoro.'
  },
  {
    title: 'Assistenza umana',
    description: 'Persone vere che rispondono alle tue domande, non bot automatici.'
  },
  {
    title: 'Sostenibilità',
    description: 'Prodotti durevoli realizzati con materiali di qualità, non fast fashion.'
  }
];

export default function ManifestoPage() {
  return (
    <>
      <Helmet>
        <title>Il Nostro Manifesto | Rarifex - L'Eccellenza Artigianale Italiana</title>
        <meta name="description" content="Scopri i valori che guidano Rarifex: italianità autentica, cura artigianale, qualità garantita e attenzione al cliente. Il nostro impegno per il Made in Italy." />
        <meta name="keywords" content="manifesto rarifex, valori rarifex, made in italy, artigianato italiano, eccellenza italiana" />
        <link rel="canonical" href="https://www.rarifex.com/manifesto" />
      </Helmet>

      <div className="min-h-screen bg-white">
        
        {/* HERO SECTION */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#008080]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Italian flag colors accent */}
              <div className="flex items-center justify-center gap-1 mb-8">
                <div className="w-3 h-8 bg-green-500 rounded-full" />
                <div className="w-3 h-8 bg-white rounded-full" />
                <div className="w-3 h-8 bg-red-500 rounded-full" />
              </div>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-8">
                Il Nostro{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00CED1] to-[#008080]">
                  Manifesto
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10">
                Crediamo che ogni oggetto fatto a mano racconti una storia. 
                Una storia di passione, tradizione e maestria che merita di essere preservata e condivisa.
              </p>
              
              <div className="flex items-center justify-center">
                <Quote className="w-8 h-8 text-[#008080] opacity-50" />
              </div>
            </div>
          </div>
        </section>

        {/* INTRO QUOTE */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <blockquote className="text-2xl md:text-3xl lg:text-4xl font-light text-slate-700 leading-relaxed italic">
                "In un mondo di produzione di massa, scegliamo l'unicità. 
                In un'epoca di velocità, scegliamo la cura. 
                In un mercato globale, scegliamo l'Italia."
              </blockquote>
              <div className="mt-8 flex items-center justify-center gap-2 text-slate-500">
                <Gem className="w-5 h-5 text-[#008080]" />
                <span className="font-medium">La filosofia Rarifex</span>
              </div>
            </div>
          </div>
        </section>

        {/* CORE VALUES */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Heart className="w-4 h-4" />
                  I Nostri Valori
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Ciò in cui crediamo
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Quattro pilastri che guidano ogni nostra scelta e definiscono chi siamo
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {CORE_VALUES.map((value, index) => (
                  <Card 
                    key={index} 
                    className="p-8 hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-slate-50 group"
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <value.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* IMAGE + TEXT SECTION - Made in Italy */}
        <section className="py-16 md:py-24 bg-slate-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <span className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <MapPin className="w-4 h-4" />
                    Made in Italy
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    L'Italia nelle mani dei suoi artigiani
                  </h2>
                  <p className="text-lg text-slate-300 leading-relaxed mb-6">
                    L'Italia è famosa nel mondo per il suo gusto, la sua creatività e la sua eccellenza manifatturiera. 
                    Ma dietro questo prestigio ci sono persone reali: artigiani che ogni giorno, nei loro laboratori, 
                    perpetuano tradizioni secolari fondendole con innovazione e creatività.
                  </p>
                  <p className="text-lg text-slate-300 leading-relaxed mb-8">
                    <strong className="text-white">Rarifex nasce per dare voce a questi maestri.</strong> Per creare un ponte 
                    tra chi crea con passione e chi cerca autenticità. Per dimostrare che il "fatto a mano" 
                    non è un lusso del passato, ma una scelta consapevole per il presente.
                  </p>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#00CED1]">100%</div>
                      <div className="text-sm text-slate-400">Made in Italy</div>
                    </div>
                    <div className="w-px h-12 bg-slate-700" />
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#00CED1]">Zero</div>
                      <div className="text-sm text-slate-400">Intermediari</div>
                    </div>
                    <div className="w-px h-12 bg-slate-700" />
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#00CED1]">∞</div>
                      <div className="text-sm text-slate-400">Storie Uniche</div>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#008080]/20 to-amber-500/20 rounded-3xl blur-2xl" />
                    <img 
                      src="https://images.unsplash.com/photo-1611523794717-4d1f87dabf3b?w=800&q=80" 
                      alt="Artigiano italiano al lavoro"
                      className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OUR COMMITMENT */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#008080]/5">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#008080]/20 to-green-500/20 rounded-3xl blur-2xl" />
                    <img 
                      src="https://images.unsplash.com/photo-1632661010878-26304779f821?w=800&q=80" 
                      alt="Dettaglio lavorazione artigianale"
                      className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                    />
                  </div>
                </div>
                <div>
                  <span className="inline-flex items-center gap-2 bg-[#008080]/10 text-[#008080] px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Award className="w-4 h-4" />
                    Il Nostro Impegno
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    Promesse che manteniamo
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-8">
                    Non sono solo parole. Sono impegni concreti che prendiamo con te, 
                    con i nostri artigiani e con la tradizione che rappresentiamo.
                  </p>
                  
                  <div className="space-y-4">
                    {COMMITMENTS.map((commitment, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                        <div className="w-8 h-8 bg-[#008080]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-[#008080]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{commitment.title}</h4>
                          <p className="text-sm text-slate-600">{commitment.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CUSTOMER CARE */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 bg-rose-500/10 text-rose-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HandHeart className="w-4 h-4" />
                Per Te
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Sei al centro di tutto
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl mx-auto">
                Non sei un cliente. Sei un appassionato come noi, qualcuno che riconosce il valore 
                delle cose fatte con cura. Per questo meriti un'esperienza all'altezza delle tue aspettative.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <Card className="p-6 text-center border-0 bg-gradient-to-b from-rose-50 to-white">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-6 h-6 text-rose-500" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Ascolto Attivo</h3>
                  <p className="text-sm text-slate-600">
                    Ogni feedback è prezioso. Le tue opinioni plasmano il nostro servizio.
                  </p>
                </Card>
                
                <Card className="p-6 text-center border-0 bg-gradient-to-b from-[#008080]/5 to-white">
                  <div className="w-12 h-12 bg-[#008080]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-[#008080]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Garanzia Totale</h3>
                  <p className="text-sm text-slate-600">
                    Acquisti protetti, resi semplici, assistenza sempre disponibile.
                  </p>
                </Card>
                
                <Card className="p-6 text-center border-0 bg-gradient-to-b from-amber-50 to-white">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Leaf className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Scelta Consapevole</h3>
                  <p className="text-sm text-slate-600">
                    Ogni acquisto sostiene l'artigianato italiano e un modello sostenibile.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 bg-gradient-to-br from-[#008080] via-[#006666] to-[#004d4d] text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Unisciti a noi
              </h2>
              <p className="text-xl text-white/80 mb-10 leading-relaxed">
                Scopri il piacere di possedere qualcosa di unico. Qualcosa che ha una storia, 
                un'anima, un valore che va oltre il prezzo.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-white text-[#008080] hover:bg-slate-100 px-8">
                    Esplora le Creazioni
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/plans">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
                    Diventa Artigiano
                  </Button>
                </Link>
              </div>
              
              <p className="mt-10 text-white/60 text-sm">
                Rarifex - Dove ogni creazione racconta una storia italiana
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
